import * as O from "fp-ts/Option"
import * as F from "fp-ts/function"
import { match } from "ts-pattern"
import { DeepPartial, untag } from "../../_common/utility-types"
import { WebQuoteFormData } from "../../../components/WebQuoteForm/WebQuoteForm"
import { BookingFormData } from "../../../components/BookingForm/BookingForm"

export type BookingData = DeepPartial<BookingFormData>
export type WebQuoteData = DeepPartial<WebQuoteFormData>
export type CourtesyHoldData = BookingData
export type BookingFormTagged = BookingData & { type: "booking" }
export type WebQuoteFormTagged = WebQuoteData & { type: "web-quote" }
export type CourtesyHoldFormTagged = CourtesyHoldData & { type: "courtesyHold" }
export type FormData = BookingFormTagged | WebQuoteFormTagged | CourtesyHoldFormTagged

export const mapToWebQuote = (
    mostRecent: O.Option<FormData>,
    lastMappedWebQuote: O.Option<WebQuoteFormTagged>,
): O.Option<WebQuoteData> =>
    F.pipe(
        mostRecent,
        O.map(selectWebQuote),
        O.map((mostRecent) => {
            const lastMappedOrUndefined = F.pipe(lastMappedWebQuote, O.map(untag), O.toNullable)
            return Object.assign({}, lastMappedOrUndefined, mostRecent)
        }),
    )

export const mapToBooking = (
    mostRecent: O.Option<FormData>,
    lastMappedBooking: O.Option<BookingFormTagged>,
): O.Option<BookingData> =>
    F.pipe(
        mostRecent,
        O.map(selectBooking),
        O.map((mostRecent) => {
            const lastMappedOrUndefined = F.pipe(lastMappedBooking, O.map(untag), O.toNullable)
            return Object.assign({}, lastMappedOrUndefined, mostRecent)
        }),
    )

export const mapToCourtesyHold = (
    mostRecent: O.Option<FormData>,
    lastMappedCourtesyHold: O.Option<CourtesyHoldFormTagged>,
): O.Option<BookingData> =>
    F.pipe(
        mostRecent,
        O.map(selectCourtesyHold),
        O.map((mostRecent) => {
            const lastMappedOrUndefined = F.pipe(lastMappedCourtesyHold, O.map(untag), O.toNullable)
            return Object.assign({}, lastMappedOrUndefined, mostRecent)
        }),
    )

const selectWebQuote = (formData: FormData): WebQuoteData =>
    match(formData)
        .with({ type: "booking" }, bookingToWebQuote)
        .with({ type: "courtesyHold" }, courtesyHoldToWebQuote)
        .with({ type: "web-quote" }, untag)
        .exhaustive()

const selectBooking = (formData: FormData): BookingData =>
    match(formData)
        .with({ type: "booking" }, untag)
        .with({ type: "courtesyHold" }, untag)
        .with({ type: "web-quote" }, webQuoteToBooking)
        .exhaustive()

const selectCourtesyHold = (formData: FormData): BookingData =>
    match(formData)
        .with({ type: "courtesyHold" }, untag)
        .with({ type: "booking" }, untag)
        .with({ type: "web-quote" }, webQuoteToCourtesyHold)
        .exhaustive()

const bookingToWebQuote = (bk: BookingFormTagged): WebQuoteData => ({
    title: bk.mainGuest?.title,
    firstName: bk.mainGuest?.firstName,
    lastName: bk.mainGuest?.lastName,
    phone: bk.mainGuest?.phone,
    email: bk.mainGuest?.email,
})

const webQuoteToBooking = (wq: WebQuoteFormTagged): BookingData => ({
    mainGuest: {
        title: wq.title,
        firstName: wq.firstName,
        lastName: wq.lastName,
        phone: wq.phone,
        email: wq.email,
    },
    confirmEmail: wq.email,
})

const webQuoteToCourtesyHold = (wq: WebQuoteFormTagged): CourtesyHoldData => webQuoteToBooking(wq)

const courtesyHoldToWebQuote = ({ type: _, ...ch }: CourtesyHoldFormTagged): WebQuoteData =>
    bookingToWebQuote({ type: "booking", ...ch })
